import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Plus, Printer } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Loading } from "@/components/common";
import { Action, Panel, PanelHeader } from "./ui";
import { dateTimeBR, dayBR } from "./MeetingBits";
import { DiscussedBlocks } from "./MinutesDiscussed";

export default function MeetingMinutes() {
  const { meetingId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data: detail } = await api.get(`/portal/meetings/${meetingId}/minutes`);
      setData(detail);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  const download = async () => {
    try {
      const response = await api.get(`/portal/meetings/${meetingId}/minutes.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `ata-reuniao-clinica-${meetingId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addRectification = async () => {
    if (!text.trim()) {
      toast.error("Escreva o texto da retificação.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/portal/meetings/${meetingId}/rectifications`, { text });
      toast.success("Retificação registrada.");
      setText("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  if (!data) return <Loading label="Carregando ata…" />;
  const m = data.minutes;

  return (
    <div data-testid="meeting-minutes-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <button
          type="button"
          data-testid="minutes-back"
          onClick={() => navigate("/portal/reunioes")}
          className="inline-flex items-center gap-2 text-[14px] font-semibold text-ink-600 hover:text-sage-700"
        >
          <ArrowLeft size={16} aria-hidden="true" /> Reuniões Clínicas
        </button>
        <div className="flex flex-wrap gap-2.5">
          <Action variant="secondary" icon={Printer} data-testid="minutes-print" onClick={() => window.print()}>
            Imprimir
          </Action>
          <Action icon={Download} data-testid="minutes-download" onClick={download}>
            Baixar PDF
          </Action>
        </div>
      </div>

      <Panel testId="minutes-document" className="space-y-6">
        <header>
          <h1 data-testid="minutes-title" className="font-display text-[26px] font-semibold text-ink-900">
            {m.title}
          </h1>
          <p className="mt-2 text-[14.5px] text-ink-700">Data: {dayBR(m.opened_at)}</p>
          <p className="text-[14.5px] text-ink-700">
            Início: {dateTimeBR(m.opened_at)} · Término: {dateTimeBR(m.closed_at)}
          </p>
          <p className="text-[14.5px] text-ink-700">Reunião aberta por: {m.opened_by_name || "—"}</p>
          <p data-testid="minutes-participants" className="text-[14.5px] text-ink-700">
            Participantes:{" "}
            {(m.participants || []).map((p) => `${p.name} (${p.staff_type_label || ""})`).join(", ") || "—"}
          </p>
          <p data-testid="minutes-count" className="text-[14.5px] font-semibold text-ink-800">
            Pacientes discutidos: {m.discussed_count}
          </p>
        </header>

        <section className="space-y-4">
          <PanelHeader label="Registro" title="Pacientes discutidos" />
          {(m.discussed || []).length === 0 ? (
            <p className="text-[14px] text-ink-500">Nenhum paciente foi discutido nesta reunião.</p>
          ) : (
            <DiscussedBlocks items={m.discussed} />
          )}
        </section>

        <section className="space-y-3">
          <PanelHeader label="Pendências" title="Pacientes prioritários não discutidos" />
          {(m.priority_not_discussed || []).length === 0 ? (
            <p data-testid="minutes-priority-empty" className="text-[14px] text-ink-500">
              Nenhum paciente prioritário permaneceu sem discussão.
            </p>
          ) : (
            <ul data-testid="minutes-priority" className="space-y-1.5 text-[14px] text-ink-800">
              <li data-testid="minutes-priority-total" className="font-semibold">
                Total: {m.priority_not_discussed_total ?? m.priority_not_discussed.length} paciente(s) prioritário(s)
                sem discussão
                {(m.priority_not_discussed_total ?? 0) > m.priority_not_discussed.length
                  ? ` · lista dos ${m.priority_not_discussed.length} casos mais antigos`
                  : ""}
              </li>
              {m.priority_not_discussed.map((p) => (
                <li key={p.patient_id}>
                  {p.full_name} —{" "}
                  {p.never_discussed ? "nunca discutido" : `há ${p.days_without_discussion} dias sem discussão`}
                </li>
              ))}
            </ul>
          )}
        </section>

        {(data.rectifications || []).length ? (
          <section className="space-y-3">
            <PanelHeader label="Correções" title="Retificações" />
            <ul data-testid="minutes-rectifications" className="space-y-2 text-[14px] text-ink-800">
              {data.rectifications.map((r, i) => (
                <li key={i} className="rounded-xl bg-sand/50 px-3.5 py-2.5">
                  {r.text}
                  <span className="block text-[13px] text-ink-500">
                    {dateTimeBR(r.created_at)} · {r.author_name || "—"}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </Panel>

      {data.status === "closed" ? (
        <Panel testId="minutes-rectification-form" className="space-y-4 print:hidden">
          <PanelHeader
            label="Retificação"
            title="Adicionar retificação"
            hint="O conteúdo original é preservado: a retificação é acrescentada com autor, data e hora."
          />
          <textarea
            data-testid="minutes-rectification-input"
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="w-full rounded-xl border border-border bg-white px-3.5 py-3 text-[14.5px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15"
            placeholder="Descreva a correção necessária"
          />
          <Action icon={Plus} data-testid="minutes-rectification-save" disabled={saving} onClick={addRectification}>
            {saving ? "Registrando…" : "Registrar retificação"}
          </Action>
        </Panel>
      ) : null}
    </div>
  );
}
